// コロプレスのスタイル構築と値→色の解決（純関数・DuckDB/ブラウザ非依存）。
//
// 役割: クラス分けの材料（数値の breaks か カテゴリ一覧）と配色から、地図描画と凡例表示に使う
//       style オブジェクトを組み立てる。描画時は colorForValue が各フィーチャ値を色へ写す。
// 関係: 材料の算出（DuckDB クエリ）は data/layer-service.computeChoroplethStyle が担い、ここは
//       純ロジックに徹する。Layers/index.js が colorForValue で getFillColor を作る。node --test 対象。
import { sampleScheme, schemeKind } from './color-schemes.js'

const DEFAULT_OPACITY = 180
const NULL_COLOR = [200, 200, 200]

// 数値を読みやすく整形する（凡例ラベル用）。BigInt も受ける。
function formatNumber(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  // 整数は桁区切り、非整数は有効桁を抑える。
  if (Number.isInteger(n)) return n.toLocaleString('en-US')
  const abs = Math.abs(n)
  const digits = abs >= 100 ? 0 : abs >= 1 ? 2 : 4
  return n.toLocaleString('en-US', { maximumFractionDigits: digits })
}

// 数値クラスの凡例ラベルを作る（breaks は内側の n-1 閾値）。
function numericLegendLabels(breaks) {
  const n = breaks.length + 1
  const labels = []
  for (let i = 0; i < n; i += 1) {
    if (i === 0) labels.push(`< ${formatNumber(breaks[0])}`)
    else if (i === n - 1) labels.push(`≥ ${formatNumber(breaks[breaks.length - 1])}`)
    else labels.push(`${formatNumber(breaks[i - 1])} – ${formatNumber(breaks[i])}`)
  }
  return labels
}

// style オブジェクトを構築する。
// 数値: { mode:'numeric', breaks:[…] }、カテゴリ: { mode:'categorical', categories:[…] }。
export function buildChoroplethStyle({
  column,
  mode,
  breaks = [],
  categories = [],
  scheme,
  opacity = DEFAULT_OPACITY,
}) {
  const resolvedScheme = scheme ?? (mode === 'categorical' ? 'Set2' : 'YlOrRd')
  const count = mode === 'categorical' ? categories.length : breaks.length + 1
  const colors = sampleScheme(resolvedScheme, Math.max(1, count))

  const legend =
    mode === 'categorical'
      ? categories.map((c, i) => ({ color: colors[i], label: String(c) }))
      : numericLegendLabels(breaks).map((label, i) => ({ color: colors[i], label }))

  return {
    type: 'choropleth',
    column,
    mode,
    breaks: mode === 'numeric' ? breaks : undefined,
    categories: mode === 'categorical' ? categories.map((c) => String(c)) : undefined,
    colors,
    scheme: resolvedScheme,
    schemeKind: schemeKind(resolvedScheme),
    opacity,
    nullColor: NULL_COLOR,
    legend,
  }
}

// 分類条件を保ったまま配色だけを作り直す（詳細パネルのスキーム変更用）。
export function recolorChoroplethStyle(style, scheme) {
  if (style?.type !== 'choropleth') return style
  return buildChoroplethStyle({
    column: style.column,
    mode: style.mode,
    breaks: style.breaks ?? [],
    categories: style.categories ?? [],
    scheme,
    opacity: style.opacity,
  })
}

// 数値 v が breaks（昇順の内側閾値）で何番目のクラスかを返す。
// 区間は [b_{i-1}, b_i) の右開き。v < breaks[0] は 0、v ≥ 最後の閾値は最終クラス。
function classIndex(v, breaks) {
  for (let i = 0; i < breaks.length; i += 1) {
    if (v < Number(breaks[i])) return i
  }
  return breaks.length
}

// フィーチャ属性値 → 描画色 [r,g,b,a]。
// 数値は breaks でクラス判定（Number 強制で BigInt 対応）、カテゴリは文字列一致。
// null・未知カテゴリ・非数値は nullColor を返す。
export function colorForValue(value, style) {
  const a = style.opacity ?? DEFAULT_OPACITY
  const withAlpha = (rgb) => [rgb[0], rgb[1], rgb[2], a]
  if (value == null) return withAlpha(style.nullColor ?? NULL_COLOR)

  if (style.mode === 'categorical') {
    const idx = (style.categories ?? []).indexOf(String(value))
    if (idx < 0) return withAlpha(style.nullColor ?? NULL_COLOR)
    return withAlpha(style.colors[idx])
  }

  const n = Number(value)
  if (!Number.isFinite(n)) return withAlpha(style.nullColor ?? NULL_COLOR)
  const idx = classIndex(n, style.breaks ?? [])
  return withAlpha(style.colors[idx] ?? style.nullColor ?? NULL_COLOR)
}
